// BOORO: the team as a canvas. The Chief stands above everything; offices hold
// their groups and their PC (with one office there is no frame around it), the agents sit in them as cards with the lead on
// top. Offices and groups stay where the person drops them, an agent dragged
// onto a group joins it, and every name is renamed with a double click.
import type { LayoutNode, OrgScope } from "@booro/team";
import {
  agentInScope,
  agentNodeId,
  groupNodeId,
  groupWithDescendants,
  layoutOrg,
  officeNodeId,
  PADDING,
} from "@booro/team";
import { Plural, Trans, useLingui } from "@lingui/react/macro";
import type { OrgAgent, OrgGroup, OrgSnapshot } from "@rakazo/contracts";
import { GROUP_COLORS } from "@rakazo/contracts";
import {
  BotAvatar,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  Input,
} from "@rakazo/ui-web";
import type { Node, NodeProps } from "@xyflow/react";
import {
  Background,
  Controls,
  Handle,
  MiniMap,
  Position,
  ReactFlow,
  ReactFlowProvider,
  useNodesState,
  useReactFlow,
  useStore,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  BookOpen,
  Building,
  Crown,
  Monitor,
  MoreHorizontal,
  Palette,
  Plus,
  Users,
} from "lucide-react";
import type { CSSProperties } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  absoluteRects,
  buildTeamEdges,
  TEAM_EDGE_TYPES,
  TEAM_HIERARCHY_SOURCE,
  TEAM_HIERARCHY_TARGET,
} from "./team-hierarchy";

export type TeamLens = "status" | "knowledge";

/** What the canvas asks its host to do; the host talks to the server. */
export type TeamCanvasActions = {
  openAgent: (agent: OrgAgent) => void;
  addGroup: (spaceId: string, parentGroupId: string | null) => void;
  addAgent: (spaceId: string, groupId: string | null) => void;
  editGroup: (group: OrgGroup, mode: "lead" | "remove") => void;
  /** Lets the person pick the lead of an office from a list. */
  chooseOfficeLead: (spaceId: string) => void;
  /** Makes an agent the lead of the group or office it sits in, or takes the lead away. */
  setLead: (agent: OrgAgent, lead: boolean) => void;
  setGroupColor: (group: OrgGroup, color: string | null) => void;
  setOfficeColor: (spaceId: string, color: string | null) => void;
  renameGroup: (group: OrgGroup, name: string) => Promise<void>;
  renameOffice: (spaceId: string, name: string) => Promise<void>;
  renameAgent: (agent: OrgAgent, name: string) => Promise<void>;
  placeAgent: (agent: OrgAgent, groupId: string | null) => Promise<void>;
  moveGroup: (
    group: OrgGroup,
    parentGroupId: string | null,
    place: { x: number; y: number },
  ) => Promise<void>;
  placeOffice: (spaceId: string, place: { x: number; y: number }) => Promise<void>;
  /**
   * Keeps the places the canvas chose for groups and offices the person has
   * not moved yet, so moving one never makes the others jump.
   */
  keepPlaces: (places: {
    groups: { group: OrgGroup; x: number; y: number }[];
    offices: { spaceId: string; x: number; y: number }[];
  }) => Promise<void>;
  /** A drop into another office, which an agent or a group cannot make. */
  refuse: () => void;
};

/** The overview map only helps once the canvas no longer fits on one screen. */
const MINIMAP_FROM_AGENTS = 30;

/** Below this zoom, agent cards shrink to their face. */
const FAR_ZOOM = 0.5;

/** The light an agent carries, with the words that belong to it. */
export function useAgentLightLabels(): Record<OrgAgent["light"], string> {
  const { t } = useLingui();
  return {
    busy: t`Working`,
    waiting: t`Needs an answer`,
    problem: t`Problem`,
    stuck: t`Stuck`,
    idle: t`Idle`,
  };
}

function lightTone(light: OrgAgent["light"]): string {
  return light === "problem"
    ? "bg-destructive"
    : light === "waiting" || light === "stuck"
      ? "bg-warning"
      : light === "busy"
        ? "bg-status-working"
        : "bg-success";
}

/** The dot that shows the state of an agent. */
export function AgentLightDot({
  light,
  className = "",
}: {
  light: OrgAgent["light"];
  className?: string;
}) {
  const labels = useAgentLightLabels();
  return (
    <span
      role="img"
      aria-label={labels[light]}
      title={labels[light]}
      className={`inline-block size-2 shrink-0 rounded-full ${lightTone(light)} ${className}`}
    />
  );
}

/** Marks an agent that works on a PC of its own instead of the office PC. */
export function OwnComputerMark({ className = "" }: { className?: string }) {
  const { t } = useLingui();
  return (
    <Monitor
      size={12}
      role="img"
      aria-label={t`Own PC`}
      className={`shrink-0 text-muted-foreground ${className}`}
    >
      <title>{t`Own PC`}</title>
    </Monitor>
  );
}

/** A group in its colour: a tinted surface with a coloured edge. */
function groupStyle(color: string | null): CSSProperties | undefined {
  if (!color) return undefined;
  return {
    borderColor: `color-mix(in srgb, ${color} 70%, transparent)`,
    backgroundColor: `color-mix(in srgb, ${color} 9%, transparent)`,
  };
}

/** A name that turns into a field on a double click; Enter keeps it, Escape drops it. */
export function InlineName({
  value,
  label,
  onSave,
  className = "",
}: {
  value: string;
  label: string;
  onSave: (name: string) => Promise<void> | void;
  className?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);
  if (editing) {
    const finish = (keep: boolean) => {
      setEditing(false);
      const next = draft.trim();
      if (keep && next && next !== value) void onSave(next);
    };
    return (
      <Input
        autoFocus
        aria-label={label}
        value={draft}
        maxLength={80}
        onFocus={(event) => event.target.select()}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={() => finish(true)}
        onKeyDown={(event) => {
          event.stopPropagation();
          if (event.key === "Enter") finish(true);
          if (event.key === "Escape") finish(false);
        }}
        className="nodrag nopan h-7 min-w-0 px-1.5 text-[13.5px]"
      />
    );
  }
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: the double click is a shortcut; the list renames too.
    <span
      title={label}
      onDoubleClick={(event) => {
        event.stopPropagation();
        setEditing(true);
      }}
      className={`min-w-0 truncate ${className}`}
      dir="auto"
    >
      {value}
    </span>
  );
}

type OfficeData = {
  name: string;
  spaceId: string;
  color: string | null;
  agents: number;
  attention: number;
  /** Whether agents of the office share its PC. */
  officeComputer: boolean;
  /** The only office: no frame, no head, only the place to drop into. */
  only: boolean;
  dimmed: boolean;
  dropTarget: boolean;
  actions: TeamCanvasActions;
};
type GroupData = {
  group: OrgGroup;
  agents: number;
  dropTarget: boolean;
  actions: TeamCanvasActions;
};
type AgentData = {
  agent: OrgAgent;
  lens: TeamLens;
  lead: boolean;
  leadColor: string | null;
  dimmed: boolean;
  highlighted: boolean;
  actions: TeamCanvasActions;
};
type ChiefData = { agent: OrgAgent; severalOffices: boolean; actions: TeamCanvasActions };

type OfficeNode = Node<OfficeData, "teamOffice">;
type GroupNode = Node<GroupData, "teamGroup">;
type AgentNode = Node<AgentData, "teamAgent">;
type ChiefNode = Node<ChiefData, "teamChief">;
type CanvasNode = OfficeNode | GroupNode | AgentNode | ChiefNode;

/** What can be added to an office or a group. */
export function AddMenu({
  spaceId,
  groupId,
  actions,
  label,
}: {
  spaceId: string;
  groupId: string | null;
  actions: TeamCanvasActions;
  label: string;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className="nodrag"
        aria-label={label}
        title={label}
        render={<Button size="icon-xs" variant="ghost" />}
      >
        <Plus />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-48">
        <DropdownMenuItem onClick={() => actions.addAgent(spaceId, groupId)}>
          <Plus />
          <Trans>Agent</Trans>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => actions.addGroup(spaceId, groupId)}>
          <Users />
          <Trans context="organigram">Group</Trans>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** What can be done with a group, on the canvas and in the list. */
export function GroupMenu({
  group,
  actions,
  className,
}: {
  group: OrgGroup;
  actions: TeamCanvasActions;
  className?: string;
}) {
  const { t } = useLingui();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={className}
        aria-label={t`Actions for ${group.name}`}
        render={<Button variant="ghost" size="icon-xs" />}
      >
        <MoreHorizontal />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-52">
        <DropdownMenuItem onClick={() => actions.editGroup(group, "lead")}>
          <Crown />
          <Trans>Choose lead</Trans>
        </DropdownMenuItem>
        <ColorMenu current={group.color} onPick={(color) => actions.setGroupColor(group, color)} />
        <DropdownMenuSeparator />
        <DropdownMenuItem variant="destructive" onClick={() => actions.editGroup(group, "remove")}>
          <Trans>Remove</Trans>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** The colours of the ghosts for a group or an office, and none. */
function ColorMenu({
  current,
  onPick,
}: {
  current: string | null;
  onPick: (color: string | null) => void;
}) {
  const { t } = useLingui();
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>
        <Palette />
        <Trans>Colour</Trans>
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="min-w-48">
        <div className="grid grid-cols-6 place-items-center gap-2 p-2">
          {GROUP_COLORS.map((color) => (
            <button
              key={color}
              type="button"
              aria-label={t`Colour ${color}`}
              aria-pressed={current === color}
              onClick={() => onPick(color)}
              className={`size-6 rounded-full border transition-transform hover:scale-110 active:scale-95 focus-visible:ring-2 focus-visible:ring-ring ${
                current === color
                  ? "scale-105 border-transparent ring-2 ring-foreground ring-offset-2 ring-offset-popover"
                  : "border-border"
              }`}
              style={{ backgroundColor: color }}
            />
          ))}
        </div>
        <DropdownMenuItem onClick={() => onPick(null)}>
          <Trans>No colour</Trans>
        </DropdownMenuItem>
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}

/** What can be done with an office on the canvas. */
function OfficeMenu({ data }: { data: OfficeData }) {
  const { t } = useLingui();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className="nodrag"
        aria-label={t`Actions for ${data.name}`}
        render={<Button variant="ghost" size="icon-xs" />}
      >
        <MoreHorizontal />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-52">
        <DropdownMenuItem onClick={() => data.actions.chooseOfficeLead(data.spaceId)}>
          <Crown />
          <Trans>Choose lead</Trans>
        </DropdownMenuItem>
        <ColorMenu
          current={data.color}
          onPick={(color) => data.actions.setOfficeColor(data.spaceId, color)}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** A point exactly on the card edge, so the lines meet the cards where they are drawn. */
const HIERARCHY_HANDLE = "size-0! min-h-0! min-w-0! border-0! opacity-0";

/** Where lines end on a card, and for an agent that can lead, where they start. */
function HierarchyHandles({ source = true }: { source?: boolean }) {
  return (
    <>
      <Handle
        id={TEAM_HIERARCHY_TARGET}
        type="target"
        position={Position.Top}
        isConnectable={false}
        className={HIERARCHY_HANDLE}
      />
      {source ? (
        <Handle
          id={TEAM_HIERARCHY_SOURCE}
          type="source"
          position={Position.Bottom}
          isConnectable={false}
          className={HIERARCHY_HANDLE}
        />
      ) : null}
    </>
  );
}

function OfficeBox({ data }: NodeProps<OfficeNode>) {
  const { t } = useLingui();
  if (data.only) {
    return (
      <>
        <HierarchyHandles source={false} />
        <div
          data-testid={`canvas-office-${data.spaceId}`}
          className={`h-full w-full rounded-3xl border-2 ${
            data.dropTarget ? "border-dashed border-primary" : "border-transparent"
          }`}
        />
      </>
    );
  }
  return (
    <>
      <HierarchyHandles source={false} />
      <div
        data-testid={`canvas-office-${data.spaceId}`}
        style={groupStyle(data.color)}
        className={`h-full w-full rounded-3xl border-2 border-dashed bg-muted/30 transition-opacity ${
          data.dropTarget ? "border-primary!" : "border-border"
        } ${data.dimmed ? "opacity-40" : ""}`}
      >
        <div className="flex h-12 cursor-grab items-center gap-2 px-4 active:cursor-grabbing">
          {data.color ? (
            <span
              aria-hidden
              className="size-2.5 shrink-0 rounded-full"
              style={{ backgroundColor: data.color }}
            />
          ) : null}
          <Building
            size={16}
            strokeWidth={1.8}
            className="shrink-0 text-muted-foreground"
            aria-hidden
          />
          <InlineName
            value={data.name}
            label={t`Rename office`}
            onSave={(name) => data.actions.renameOffice(data.spaceId, name)}
            className="text-[14px] font-semibold text-foreground"
          />
          <span className="shrink-0 text-[12px] text-muted-foreground">
            <Plural value={data.agents} one="# agent" other="# agents" />
            {data.attention > 0 ? (
              <>
                {" · "}
                <Plural value={data.attention} one="# needs you" other="# need you" />
              </>
            ) : null}
          </span>
          {data.officeComputer ? (
            <span
              title={t`On a shared PC every agent has its own screen and browser; files and programs are shared.`}
              className="flex shrink-0 items-center gap-1 text-[12px] text-muted-foreground"
            >
              <Monitor size={13} aria-hidden />
              <Trans>Office PC</Trans>
            </span>
          ) : null}
          <span className="ms-auto">
            <AddMenu
              spaceId={data.spaceId}
              groupId={null}
              actions={data.actions}
              label={t`Add to ${data.name}`}
            />
          </span>
          <OfficeMenu data={data} />
        </div>
      </div>
    </>
  );
}

function GroupBox({ data }: NodeProps<GroupNode>) {
  const { t } = useLingui();
  const { group, actions } = data;
  return (
    <>
      <HierarchyHandles source={false} />
      <div
        data-testid={`canvas-group-${group.id}`}
        style={groupStyle(group.color)}
        className={`h-full w-full rounded-2xl border-2 bg-card/70 shadow-sm ${
          data.dropTarget ? "border-primary! ring-4 ring-primary/20" : "border-border"
        }`}
      >
        <div className="flex h-12 cursor-grab items-center gap-2 px-3 active:cursor-grabbing">
          {group.color ? (
            <span
              aria-hidden
              className="size-2.5 shrink-0 rounded-full"
              style={{ backgroundColor: group.color }}
            />
          ) : null}
          {!group.color ? (
            <Users
              size={14}
              strokeWidth={1.8}
              className="shrink-0 text-muted-foreground"
              aria-hidden
            />
          ) : null}
          <InlineName
            value={group.name}
            label={t`Rename ${group.name}`}
            onSave={(name) => actions.renameGroup(group, name)}
            className="text-[14px] font-semibold text-foreground"
          />
          <span className="ms-auto shrink-0 text-[12px] text-muted-foreground">{data.agents}</span>
          <AddMenu
            spaceId={group.spaceId}
            groupId={group.id}
            actions={actions}
            label={t`Add to ${group.name}`}
          />
          <GroupMenu group={group} actions={actions} className="nodrag" />
        </div>
      </div>
    </>
  );
}

/**
 * The crown on the head of an agent card: always worn by the lead, and
 * offered to the others when the pointer or the keyboard reaches the card.
 */
function LeadToggle({
  agent,
  lead,
  actions,
}: {
  agent: OrgAgent;
  lead: boolean;
  actions: TeamCanvasActions;
}) {
  const { t } = useLingui();
  const label = lead ? t`No longer lead` : t`Make lead`;
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={lead}
      title={label}
      onClick={(event) => {
        event.stopPropagation();
        actions.setLead(agent, !lead);
      }}
      className={`nodrag absolute -top-2.5 start-5.5 grid size-5 place-items-center rounded-full border transition focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-ring ${
        lead
          ? "border-primary bg-primary text-primary-foreground"
          : "border-border bg-card text-muted-foreground opacity-0 hover:text-foreground group-hover/agent:opacity-100"
      }`}
    >
      <Crown size={11} strokeWidth={2.2} aria-hidden />
    </button>
  );
}

function AgentCard({ data }: NodeProps<AgentNode>) {
  const { t } = useLingui();
  const { agent } = data;
  const far = useStore((state) => state.transform[2] < FAR_ZOOM);
  const fade = data.dimmed ? "opacity-30" : "";
  const leadRing =
    data.lead && data.leadColor
      ? { borderColor: data.leadColor, boxShadow: `0 0 0 1px ${data.leadColor}` }
      : undefined;
  if (far) {
    return (
      <>
        <HierarchyHandles />
        <div
          data-testid={`canvas-agent-${agent.id}`}
          className={`flex h-full w-full items-center justify-center rounded-xl border border-border bg-card ${fade}`}
          title={agent.name}
        >
          <BotAvatar color={agent.color} identity={agent.id} size={40} status={agent.status} />
        </div>
      </>
    );
  }
  let subline: React.ReactNode = agent.title;
  if (data.lens === "knowledge" && data.highlighted) {
    subline = (
      <span className="flex items-center gap-1">
        <BookOpen size={11} aria-hidden />
        {t`Reads the page`}
      </span>
    );
  } else if (data.lead) {
    subline = [t`Lead`, agent.title].filter(Boolean).join(" · ");
  } else if (data.lens === "status" && agent.openTasks > 0) {
    subline = <Plural value={agent.openTasks} one="# open task" other="# open tasks" />;
  }
  return (
    <>
      <HierarchyHandles />
      <div
        data-testid={`canvas-agent-${agent.id}`}
        style={leadRing}
        className={`group/agent relative flex h-full w-full cursor-grab items-center gap-3 rounded-xl border bg-card px-3 shadow-sm active:cursor-grabbing ${
          data.highlighted ? "border-primary ring-2 ring-primary/40" : "border-border"
        } ${fade}`}
      >
        <span className="grid size-10 shrink-0 place-items-center rounded-lg bg-muted">
          <BotAvatar color={agent.color} identity={agent.id} size={32} status={agent.status} />
        </span>
        <span className="flex min-w-0 flex-1 flex-col pe-3">
          <InlineName
            value={agent.name}
            label={t`Rename ${agent.name}`}
            onSave={(name) => data.actions.renameAgent(agent, name)}
            className="text-[14px] font-semibold text-foreground"
          />
          <span className="truncate text-[11.5px] text-muted-foreground" dir="auto">
            {subline}
          </span>
        </span>
        {data.lens === "status" ? (
          <AgentLightDot light={agent.light} className="absolute end-2.5 top-2.5" />
        ) : null}
        <LeadToggle agent={agent} lead={data.lead} actions={data.actions} />
        {agent.hasOwnComputer ? <OwnComputerMark className="absolute end-2 bottom-2" /> : null}
      </div>
    </>
  );
}

function ChiefCard({ data }: NodeProps<ChiefNode>) {
  const { t } = useLingui();
  const { agent } = data;
  return (
    <div
      data-testid="canvas-chief"
      className="relative flex h-full w-full cursor-pointer items-center gap-3 rounded-2xl border-2 border-primary bg-card px-4 shadow-md"
    >
      <span className="grid size-12 shrink-0 place-items-center rounded-xl bg-muted">
        <BotAvatar color={agent.color} identity={agent.id} size={40} status={agent.status} />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="flex min-w-0 items-center gap-1.5">
          <Crown size={14} className="shrink-0 text-foreground" aria-hidden />
          <InlineName
            value={agent.name}
            label={t`Rename ${agent.name}`}
            onSave={(name) => data.actions.renameAgent(agent, name)}
            className="text-[15px] font-semibold text-foreground"
          />
        </span>
        {data.severalOffices ? (
          <span className="truncate text-[12px] text-muted-foreground">
            <Trans>Leads every office</Trans>
          </span>
        ) : null}
      </span>
      <AgentLightDot light={agent.light} className="absolute end-3 top-3" />
      <Handle
        id={TEAM_HIERARCHY_SOURCE}
        type="source"
        position={Position.Bottom}
        isConnectable={false}
        className={HIERARCHY_HANDLE}
      />
    </div>
  );
}

// React Flow styles its own "group" type; these names keep the canvas in our hands.
const NODE_TYPES = {
  teamOffice: OfficeBox,
  teamGroup: GroupBox,
  teamAgent: AgentCard,
  teamChief: ChiefCard,
};

/** The node and everything inside it, which a dragged group cannot be dropped into. */
function withInside(layout: readonly LayoutNode[], id: string): Set<string> {
  const out = new Set([id]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const node of layout) {
      if (node.parentId && out.has(node.parentId) && !out.has(node.id)) {
        out.add(node.id);
        grew = true;
      }
    }
  }
  return out;
}

/** Follow the theme the shell writes; React Flow's system mode can differ from an explicit choice. */
function useCanvasColorMode(): "light" | "dark" {
  const [mode, setMode] = useState<"light" | "dark">(() =>
    document.documentElement.dataset.theme === "light" ? "light" : "dark",
  );
  useEffect(() => {
    const root = document.documentElement;
    const read = () => (root.dataset.theme === "light" ? "light" : "dark");
    const observer = new MutationObserver(() => setMode(read()));
    observer.observe(root, { attributes: true, attributeFilter: ["data-theme"] });
    return () => observer.disconnect();
  }, []);
  return mode;
}

export function TeamCanvas(props: {
  snapshot: OrgSnapshot;
  scope: OrgScope;
  lens: TeamLens;
  /** With the knowledge lens: the agents that read the chosen page. */
  readers: ReadonlySet<string> | null;
  actions: TeamCanvasActions;
}) {
  return (
    <ReactFlowProvider>
      <CanvasInner {...props} />
    </ReactFlowProvider>
  );
}

function CanvasInner({
  snapshot,
  scope,
  lens,
  readers,
  actions,
}: {
  snapshot: OrgSnapshot;
  scope: OrgScope;
  lens: TeamLens;
  readers: ReadonlySet<string> | null;
  actions: TeamCanvasActions;
}) {
  const { t } = useLingui();
  const colorMode = useCanvasColorMode();
  const far = useStore((state) => state.transform[2] < FAR_ZOOM);
  const flow = useReactFlow();
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  // A refused or unchanged drop puts the node back where the layout wants it.
  const [layoutVersion, setLayoutVersion] = useState(0);

  const layout = useMemo(() => layoutOrg(snapshot), [snapshot]);
  const layoutById = useMemo(() => new Map(layout.map((node) => [node.id, node])), [layout]);
  const rects = useMemo(() => absoluteRects(layout), [layout]);
  const scoped = Boolean(scope.spaceId || scope.groupId || scope.botId);

  const buildNodes = useCallback((): CanvasNode[] => {
    const groupById = new Map(snapshot.groups.map((group) => [group.id, group]));
    const agentById = new Map(snapshot.agents.map((agent) => [agent.id, agent]));
    const officeById = new Map(snapshot.offices.map((office) => [office.id, office]));
    // Every agent counts for its group and every group above it.
    const countIn = (groupId: string) => {
      const inside = groupWithDescendants(snapshot.groups, groupId);
      return snapshot.agents.filter((agent) => agent.groupId && inside.has(agent.groupId)).length;
    };
    return layout.flatMap((node): CanvasNode[] => {
      const base = {
        id: node.id,
        position: { x: node.x, y: node.y },
        width: node.width,
        height: node.height,
        style: { width: node.width, height: node.height },
        ...(node.parentId ? { parentId: node.parentId } : {}),
      };
      if (node.kind === "office") {
        const spaceId = node.id.slice("office:".length);
        const office = officeById.get(spaceId);
        if (!office) return [];
        const agents = snapshot.agents.filter(
          (agent) => agent.spaceId === spaceId && agent.id !== snapshot.chiefBotId,
        );
        return [
          {
            ...base,
            type: "teamOffice",
            selectable: false,
            draggable: snapshot.offices.length > 1,
            data: {
              name: office.name,
              spaceId,
              color: office.color ?? null,
              agents: agents.length,
              attention: agents.filter((agent) => agent.light !== "busy" && agent.light !== "idle")
                .length,
              officeComputer: agents.some((agent) => agent.computerId && !agent.hasOwnComputer),
              only: snapshot.offices.length === 1,
              dimmed: Boolean(scope.spaceId && scope.spaceId !== spaceId),
              dropTarget: dropTarget === node.id,
              actions,
            },
          },
        ];
      }
      if (node.kind === "group") {
        const group = groupById.get(node.id.slice("group:".length));
        if (!group) return [];
        return [
          {
            ...base,
            type: "teamGroup",
            data: {
              group,
              agents: countIn(group.id),
              dropTarget: dropTarget === node.id,
              actions,
            },
          },
        ];
      }
      const agent = agentById.get(node.id.slice("agent:".length));
      if (!agent) return [];
      if (node.kind === "chief") {
        return [
          {
            ...base,
            type: "teamChief",
            draggable: false,
            data: { agent, severalOffices: snapshot.offices.length > 1, actions },
          },
        ];
      }
      const parentGroup = node.parentId?.startsWith("group:")
        ? groupById.get(node.parentId.slice("group:".length))
        : undefined;
      return [
        {
          ...base,
          type: "teamAgent",
          data: {
            agent,
            lens,
            lead: node.lead === true,
            leadColor:
              parentGroup?.color ??
              (node.parentId?.startsWith("office:")
                ? (officeById.get(node.parentId.slice("office:".length))?.color ?? null)
                : null),
            dimmed:
              (scoped && !agentInScope(snapshot.groups, agent, scope)) ||
              (lens === "knowledge" && readers !== null && !readers.has(agent.id)),
            highlighted: lens === "knowledge" && readers?.has(agent.id) === true,
            actions,
          },
        },
      ];
    });
    // layoutVersion puts a node back after a drop that changed nothing.
  }, [layout, snapshot, scope, scoped, lens, readers, dropTarget, actions, layoutVersion]);

  const edges = useMemo(() => buildTeamEdges(layout), [layout]);

  const [nodes, setNodes, onNodesChange] = useNodesState<CanvasNode>([]);
  useEffect(() => {
    // A node without its measured size makes React Flow measure it again and
    // drop its lines meanwhile, which would restart their pulse on every reload.
    setNodes((current) => {
      const measured = new Map(current.map((node) => [node.id, node.measured]));
      return buildNodes().map((node) => {
        const size = measured.get(node.id);
        return size ? ({ ...node, measured: size } as CanvasNode) : node;
      });
    });
  }, [buildNodes, setNodes]);

  // The filter flies to what it names.
  const fittedScope = useRef<string | null>(null);
  useEffect(() => {
    const target = scope.botId
      ? agentNodeId(scope.botId)
      : scope.groupId
        ? groupNodeId(scope.groupId)
        : scope.spaceId
          ? officeNodeId(scope.spaceId)
          : null;
    const key = target ?? "*";
    if (fittedScope.current === key) return;
    fittedScope.current = key;
    const timer = window.setTimeout(() => {
      void flow.fitView({
        ...(target ? { nodes: [{ id: target }] } : {}),
        duration: 400,
        maxZoom: target ? 1.2 : 1,
        padding: 0.12,
      });
    }, 50);
    return () => window.clearTimeout(timer);
  }, [scope.botId, scope.groupId, scope.spaceId, flow]);

  /** The deepest office or group under the pointer that the dragged node may land in. */
  const targetAt = useCallback(
    (event: MouseEvent | TouchEvent | React.MouseEvent, dragged: string): string | null => {
      const point =
        "clientX" in event
          ? { x: event.clientX, y: event.clientY }
          : { x: event.touches[0]?.clientX ?? 0, y: event.touches[0]?.clientY ?? 0 };
      const position = flow.screenToFlowPosition(point);
      const excluded = withInside(layout, dragged);
      let best: { id: string; area: number } | null = null;
      for (const node of layout) {
        if (node.kind !== "office" && node.kind !== "group") continue;
        if (excluded.has(node.id)) continue;
        const rect = rects.get(node.id);
        if (!rect) continue;
        const inside =
          position.x >= rect.x &&
          position.x <= rect.x + rect.width &&
          position.y >= rect.y &&
          position.y <= rect.y + rect.height;
        const area = rect.width * rect.height;
        if (inside && (!best || area < best.area)) best = { id: node.id, area };
      }
      return best?.id ?? null;
    },
    [flow, layout, rects],
  );

  const officeOf = useCallback(
    (containerId: string): string | null => {
      if (containerId.startsWith("office:")) return containerId.slice("office:".length);
      const group = snapshot.groups.find((entry) => groupNodeId(entry.id) === containerId);
      return group?.spaceId ?? null;
    },
    [snapshot.groups],
  );

  /** The office a dragged agent or group belongs to; it cannot leave it. */
  const homeOf = useCallback(
    (node: CanvasNode): string | null =>
      node.type === "teamAgent"
        ? node.data.agent.spaceId
        : node.type === "teamGroup"
          ? node.data.group.spaceId
          : null,
    [],
  );

  const drop = useCallback(
    async (dragged: CanvasNode, target: string | null) => {
      setDropTarget(null);
      try {
        if (dragged.type === "teamOffice") {
          await actions.keepPlaces({
            groups: [],
            offices: snapshot.offices
              .filter((office) => office.id !== dragged.data.spaceId)
              .filter((office) => typeof office.x !== "number" || typeof office.y !== "number")
              .flatMap((office) => {
                const node = layoutById.get(officeNodeId(office.id));
                return node ? [{ spaceId: office.id, x: node.x, y: node.y }] : [];
              }),
          });
          await actions.placeOffice(dragged.data.spaceId, {
            x: Math.round(dragged.position.x),
            y: Math.round(dragged.position.y),
          });
          return;
        }
        if (!target) return;
        if (officeOf(target) !== homeOf(dragged)) {
          actions.refuse();
          return;
        }
        const groupId = target.startsWith("group:") ? target.slice("group:".length) : null;
        if (dragged.type === "teamAgent") {
          const agent = dragged.data.agent;
          if (agent.groupId !== groupId) await actions.placeAgent(agent, groupId);
          return;
        }
        if (dragged.type === "teamGroup") {
          const group = dragged.data.group;
          // The place inside the target, measured from where its subgroups start.
          const container = layoutById.get(target);
          const targetRect = rects.get(target);
          const absolute = flow.getInternalNode(dragged.id)?.internals.positionAbsolute;
          if (!container || !targetRect || !absolute) return;
          // The other groups of the target keep the places they have now.
          await actions.keepPlaces({
            offices: [],
            groups: snapshot.groups
              .filter(
                (sibling) =>
                  sibling.id !== group.id &&
                  sibling.spaceId === group.spaceId &&
                  (sibling.parentGroupId ?? null) === groupId &&
                  (sibling.x === null || sibling.y === null),
              )
              .flatMap((sibling) => {
                const node = layoutById.get(groupNodeId(sibling.id));
                return node
                  ? [
                      {
                        group: sibling,
                        x: Math.max(0, node.x - PADDING),
                        y: Math.max(0, node.y - (container.childrenTop ?? 0)),
                      },
                    ]
                  : [];
              }),
          });
          await actions.moveGroup(group, groupId, {
            x: Math.max(0, Math.round(absolute.x - targetRect.x - PADDING)),
            y: Math.max(0, Math.round(absolute.y - targetRect.y - (container.childrenTop ?? 0))),
          });
        }
      } finally {
        setLayoutVersion((version) => version + 1);
      }
    },
    [actions, officeOf, homeOf, layoutById, rects, flow, snapshot],
  );

  return (
    <div data-testid="team-canvas" data-far={far || undefined} className="h-full w-full">
      <ReactFlow<CanvasNode>
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        nodeTypes={NODE_TYPES}
        edgeTypes={TEAM_EDGE_TYPES}
        onNodeDrag={(event, node) => {
          if (node.type === "teamOffice") return;
          // Only the office the node belongs to lights up as a place to drop it.
          const target = targetAt(event, node.id);
          setDropTarget(target && officeOf(target) === homeOf(node) ? target : null);
        }}
        onNodeDragStop={(event, node) =>
          void drop(node, node.type === "teamOffice" ? null : targetAt(event, node.id))
        }
        onNodeClick={(_event, node) => {
          if (node.type === "teamAgent" || node.type === "teamChief") {
            actions.openAgent(node.data.agent);
          }
        }}
        nodesConnectable={false}
        zoomOnDoubleClick={false}
        fitView
        minZoom={0.1}
        maxZoom={1.6}
        colorMode={colorMode}
        attributionPosition="bottom-center"
        className="bg-background!"
        aria-label={t`Team canvas`}
      >
        <Background gap={24} size={1} className="bg-background! text-border" color="currentColor" />
        <Controls position="bottom-left" showInteractive={false} />
        {snapshot.agents.length > MINIMAP_FROM_AGENTS ? (
          <MiniMap
            position="top-right"
            pannable
            zoomable
            nodeClassName={(node) =>
              node.type === "teamAgent" ? "fill-foreground/50" : "fill-muted-foreground/15"
            }
            className="overflow-hidden rounded-xl border border-border bg-card!"
            ariaLabel={t`Overview of the canvas`}
          />
        ) : null}
      </ReactFlow>
    </div>
  );
}
